import { useState, useEffect, useRef } from 'react';
import { MoveUp, MoveDown, RotateCcw, RotateCw, ChevronUp, ChevronDown, Home } from "lucide-react";

export default function ControlsPanel({ wsUrl, onAlert, onReady, onSendCommandReady, onTelemetry, homeLocation }) {
  const [message, setMessage] = useState('');
  const [log, setLog] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const logCommand = (cmd) => {
    const timestamp = new Date().toLocaleTimeString();
    const entry = `[${timestamp}] ${cmd}`;
    setLog((prev) => [entry, ...prev.slice(0, 49)]);
  };

  const sendCommand = (type, payload = {}) => {
    const cmd = `${type} ${JSON.stringify(payload)}`;
    logCommand(`WS Sent: ${cmd}`);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
    } else {
      logCommand('[!] WebSocket not connected');
      setMessage('WebSocket not connected');
    }
  };

  // Notify parent that sendCommand is ready
  useEffect(() => {
    if (onSendCommandReady) {
      onSendCommandReady(sendCommand);
    }
  }, []);

  const connectWebSocket = () => {
    if (!wsUrl) {
      return;
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      logCommand('[WS] Connected');
      setMessage('');
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        switch (msg.type) {
          case 'error':
            logCommand(`❌ Error: ${msg.error}`);
            setMessage(`Error: ${msg.error}`);
            break;

          case 'move':
            logCommand(`[WS] MOVE: ${msg.payload.direction}`);
            break;

          case 'camera':
            logCommand(`[WS] CAMERA: ${msg.payload.direction}`);
            break;

          case 'action':
            logCommand(`[WS] ACTION: ${msg.payload.action || "UNKNOWN"}`);
            break;

          case 'route':
            logCommand(`[WS] ROUTE received (${msg.payload.waypoints.length} waypoints)`);
            break;

          case 'log':
            logCommand(msg.payload.message);
            break;

          case 'rtc_offer':
            logCommand(`[RTC] Sending video offer..`);
            break;

          case 'rtc_answer':
            logCommand(`[RTC] Video stream connected!`);
            break;

          case 'alert': {
            const alertMessage = msg.payload?.message || "Alert received";
            logCommand(`[ALERT] ${alertMessage}`);
            if (onAlert) onAlert(alertMessage);
            break;
          }

          case 'home':
            logCommand(`[WS] HOME received`);
            break;

          case 'mode':
            logCommand(`[WS] MODE: ${msg.payload.mode}`);
            break;

          case 'telemetry':
            logCommand(`[WS] TELEMETRY received`);
            if (onTelemetry) onTelemetry(msg.payload);
            break;

          default:
            logCommand(`[WS] Unknown message: ${event.data}`);
        }

      } catch {
        logCommand(`[WS] Invalid JSON: ${event.data}`);
      }
    };

    ws.onclose = () => {
      logCommand('[WS] Reconnecting...');
      setMessage('[WS] Reconnecting...');
      reconnectTimer.current = setTimeout(connectWebSocket, 3000);
    };

    ws.onerror = (err) => {
      logCommand(`WebSocket error: ${err.message}`);
      ws.close();
    };
  };

  useEffect(() => {
    connectWebSocket();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [wsUrl]);

  const move = (direction) => sendCommand('move', { direction });
  const camera = (direction) => sendCommand('camera', { direction });

  // joystick support
  const [useJoystick, setUseJoystick] = useState(false);
  const joystickRef = useRef(null);
  const joystickMouseMove = useRef(null);
  const [joystickPos, setJoystickPos] = useState({ x: 0, y: 0 });

  // mode toggle
  const [mode, setMode] = useState('Manual');

  const determineDirection = (x, y) => {
    // x,y relative to center; y negative = north
    if (Math.abs(x) > Math.abs(y)) {
      return x > 0 ? 'yaw_right' : 'yaw_left';
    } else {
      return y < 0 ? 'north' : 'hover';
    }
  };

  const onJoystickDown = (e) => {
    const rect = joystickRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const moveHandler = (ev) => {
      const dx = ev.clientX - cx;
      const dy = ev.clientY - cy;
      const dir = determineDirection(dx, dy);
      move(dir);
      // limit indicator to radius
      const r = rect.width / 2 - 8; // 8px padding
      const dist = Math.sqrt(dx * dx + dy * dy);
      const factor = dist > r ? r / dist : 1;
      setJoystickPos({ x: dx * factor, y: dy * factor });
    };
    joystickMouseMove.current = moveHandler;
    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', onJoystickUp);
  };

  const onJoystickUp = () => {
    move('hover');
    setJoystickPos({ x: 0, y: 0 });
    if (joystickMouseMove.current) {
      window.removeEventListener('mousemove', joystickMouseMove.current);
      window.removeEventListener('mouseup', onJoystickUp);
      joystickMouseMove.current = null;
    }
  };

  return (
    <div>

<h4>Drone Movement</h4>

{/* Movement Controls */}

<div
  style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  }}
>
  {/* either joystick interface or discrete directional buttons */}
  {useJoystick ? (
    <div
      ref={joystickRef}
      onMouseDown={onJoystickDown}
      style={{
        position: 'relative',
        width: 120,
        height: 120,
        border: '2px solid #888',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        cursor: 'pointer'
      }}
    >
      <span style={{ fontSize: 12, color: '#666' }}></span>
      <div
        style={{
          position: 'absolute',
          width: 16,
          height: 16,
          background: '#3b82f6',
          borderRadius: '50%',
          transform: `translate(${joystickPos.x}px, ${joystickPos.y}px)`,
          pointerEvents: 'none'
        }}
      />
    </div>
  ) : (
    <>
      {/* position arrow above the central hover button by using a 3-column grid with empty side cells */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '48px 48px 48px',
          justifyContent: 'center',
          gap: '10px'
        }}
      >
        <div />
        <button
          className="control-btn circle"
          onClick={() => move('north')}
          style={{ transform: 'translateX(-5px)' }}
        >
          <ChevronUp size={20}/>
        </button>
        <div />
      </div>

      {/* Yaw Left / Hover / Yaw Right */}
      <div
        className="control-row"
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px'
        }}
      >
        <button className="control-btn accent" onClick={() => move('yaw_left')}>
          <RotateCcw size={18} style={{ marginRight: 6 }} />
          Yaw Left
        </button>

        <button className="control-btn circle" onClick={() => move('hover')}>
          ●
        </button>

        <button className="control-btn accent" onClick={() => move('yaw_right')}>
          Yaw Right
          <RotateCw size={18} style={{ marginLeft: 6 }} />
        </button>
      </div>
    </>
  )}


  {/* Ascend / Descend */}
  <div
    className="control-row"
    style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '10px'
    }}
  >
    <button className="control-btn accent" onClick={() => move('ascend')}>
      <MoveUp size={12} style={{ marginRight: 6 }} />
      Ascend
    </button>

    <button className="control-btn accent" onClick={() => move('descend')}>
      Descend
      <MoveDown size={12} style={{ marginLeft: 6 }} />
    </button>
  </div>

  {/* joystick toggle */}
  <button
    className="control-btn"
    style={{ marginTop: 8 }}
    onClick={() => setUseJoystick((v) => !v)}
  >
    {useJoystick ? 'Use Buttons' : 'Use Joystick'}
  </button>

  {/* mode toggle */}
  <button
    className="control-btn"
    style={{ marginTop: 8, background: mode === 'Automatic' ? '#90EE90' : '#FFB6C1' }}
    onClick={() => {
      const newMode = mode === 'Manual' ? 'Automatic' : 'Manual';
      setMode(newMode);
      logCommand(`Mode: ${newMode}`);      sendCommand('mode', { mode: newMode });    }}
  >
    {mode}
  </button>

</div>



      <h4>Camera Controls</h4>

      <div className="control-row">
        <button className="control-btn" onClick={() => camera('up')}>
          <ChevronUp size={18} style={{ marginRight: 6 }} />
          Camera Up
        </button>

        <button className="control-btn" onClick={() => camera('down')}>
          Camera Down
          <ChevronDown size={18} style={{ marginLeft: 6 }} />
        </button>
      </div>

      <h4>Navigation</h4>

      <button
        className="control-btn accent"
        onClick={() => {
          if (homeLocation && homeLocation.lat && homeLocation.lon) {
            sendCommand('home', { lat: homeLocation.lat, lon: homeLocation.lon });
          } else {
            setMessage('No home location set');
          }
        }}
        style={{ width: '100%', marginBottom: 8 }}
      >
        <Home size={18} style={{ marginRight: 6 }} />
        Go Home
      </button>

      {message && <p style={{ color: 'orange' }}>{message}</p>}

      <h4>Command Log</h4>

      <div
        style={{
          backgroundColor: '#111',
          color: '#0f0',
          maxWidth: '400px',
          fontFamily: 'monospace',
          fontSize: '12px',
          padding: '10px',
          maxHeight: '250px',
          overflowY: 'auto',
          border: '1px solid #333',
          borderRadius: '4px'
        }}
      >
        {log.map((cmd, i) => (
          <div
            key={i}
            style={{ color: cmd.toLowerCase().includes('error') ? 'red' : '#0f0' }}
          >
            {cmd}
          </div>
        ))}
      </div>
    </div>
  );
}